// WhatsApp number rules for the popup's WhatsApp field.
//
// The field is prefilled "+27" and shoppers type their number the way they
// say it, "071 571 3300", so the form sent "+270715713300". Meta has no such
// number: every welcome and broadcast to it failed, and the signup's real
// WhatsApp chat landed on a second contact. Mo's SeaMoss had 61 of these on
// 13 Oct 2026, still arriving daily.
//
// Only MECHANICAL repairs are made (the kept trunk 0, the country code typed
// twice), and only where the country's national length is fixed, so the
// repaired number can be checked. Anything else is left as typed.
// api/popup-submissions.ts carries a self-contained copy (it cannot import
// src/ at runtime); keep the two in step. Same table as flowIQ's
// `flowiq contacts lint` (api/cli/contacts.js NATIONAL_LEN).

export const NATIONAL_LEN: Record<string, number> = {
  "27": 9, "351": 9, "34": 9, "44": 10, "33": 9, "31": 9, "61": 9, "971": 9,
  "254": 9, "263": 9, "260": 9, "267": 8, "258": 9, "264": 9, "230": 8,
};

// Longest code first, so "264" is not read as "26…".
const CODES = Object.keys(NATIONAL_LEN).sort((a, b) => b.length - a.length);

function countryOf(digits: string): string | null {
  return CODES.find((cc) => digits.startsWith(cc)) ?? null;
}

/** "+27 071 571 3300" → "+27715713300". Empty in, empty out. */
export function normalizeWhatsAppNumber(raw: string): string {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return "";
  // Only an international number is repaired; the form and the API refuse one without "+".
  if (!trimmed.startsWith("+")) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return trimmed;
  const cc = countryOf(digits);
  if (cc) {
    const n = NATIONAL_LEN[cc];
    const rest = digits.slice(cc.length);
    // "+27" then "0715713300": the trunk 0 belongs to dialling inside the country.
    if (rest.length === n + 1 && rest.startsWith("0")) return `+${cc}${rest.slice(1)}`;
    // "+27" then "27715713300": the code typed again.
    if (rest.length === cc.length + n && rest.startsWith(cc)) return `+${cc}${rest.slice(cc.length)}`;
    // "+27" then "+27 0715713300" style: code twice AND the trunk 0.
    if (rest.length === cc.length + n + 1 && rest.startsWith(`${cc}0`)) return `+${cc}${rest.slice(cc.length + 1)}`;
  }
  return `+${digits}`;
}

/** A plain-language problem with the number after normalising, or null. */
export function whatsAppNumberProblem(raw: string): string | null {
  const normalized = normalizeWhatsAppNumber(raw);
  const digits = normalized.replace(/\D/g, "");
  const cc = countryOf(digits);
  if (!cc) return null;
  const n = NATIONAL_LEN[cc];
  const rest = digits.slice(cc.length);
  // A kept trunk 0 is not part of the number: "+27 071571330" is 8 digits, not 9.
  const national = rest.startsWith("0") ? rest.length - 1 : rest.length;
  if (national === n && !rest.startsWith("0")) return null;
  return national < n
    ? `That number looks too short: a +${cc} number has ${n} digits after +${cc}`
    : `That number looks too long: a +${cc} number has ${n} digits after +${cc}`;
}
